import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { Reveal } from "@/components/reveal";
import { ArrowRight, ChevronRight, Sparkles } from "lucide-react";

const TITLE = "How to build a storefront that converts";
const DESCRIPTION =
  "The layout, copy, and product mix that turn casual browsers into repeat buyers on your creator storefront.";
const PATH = "/journal/how-to-build-a-storefront-that-converts";

export const Route = createFileRoute("/journal_/how-to-build-a-storefront-that-converts")({
  head: () => ({
    meta: [
      { title: `${TITLE} — Pluggz` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "Pluggz" },
          publisher: {
            "@type": "Organization",
            name: "Pluggz",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
          },
          mainEntityOfPage: `${SITE_URL}${PATH}`,
          datePublished: "2026-06-10",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}/journal` },
            { "@type": "ListItem", position: 3, name: TITLE, item: `${SITE_URL}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: ArticlePage,
});

function ArticlePage() {
  return (
    <article className="relative">
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-accent/20 blur-3xl float-slow" />

      <nav aria-label="Breadcrumb" className="relative mx-auto max-w-3xl px-6 pt-10">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <li><Link to="/" className="hover:text-foreground transition-colors">Home</Link></li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li><Link to="/journal" className="hover:text-foreground transition-colors">Journal</Link></li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li aria-current="page" className="text-foreground">Storefronts that convert</li>
        </ol>
      </nav>

      <header className="relative mx-auto max-w-3xl px-6 pt-10 pb-10 text-center">
        <Reveal>
          <p className="editorial-eyebrow inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Playbook · 7 min read
          </p>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">
            How to build a storefront that <span className="italic shimmer">converts</span>
          </h1>
        </Reveal>
        <Reveal delay={260}>
          <p className="mt-6 text-sm text-muted-foreground">By the Pluggz Editorial Team · June 2026</p>
        </Reveal>
      </header>

      <Reveal>
        <div className="relative mx-auto mb-12 max-w-5xl px-6">
          <div className="aspect-[16/7] w-full overflow-hidden rounded-3xl bg-gradient-to-br from-accent/30 via-card to-background ring-1 ring-border/60" />
        </div>
      </Reveal>

      <div className="mx-auto max-w-2xl px-6 pb-24 font-light text-foreground/90">
        <section className="prose-spacing space-y-6 leading-relaxed">
          <p className="text-lg">
            Most creator storefronts under-perform for the same reason most websites do: they were built to look complete, not to <em>convert</em>. A converting storefront is a curated argument — every product, photo, and caption is there to move a shopper one step closer to the brand's checkout. Everything else is noise.
          </p>

          <h2 className="mt-12 font-serif text-3xl">1. Lead with point of view</h2>
          <p className="text-muted-foreground">
            The strongest storefronts feel edited by a person, not assembled by a tool. Open with a short paragraph that explains what this shop is about — your aesthetic, your category, what you'd never recommend. A reader who lands on a storefront and immediately understands the editor behind it is two-to-three times more likely to tap a product than one greeted by a wall of thumbnails.
          </p>

          <h2 className="mt-12 font-serif text-3xl">2. Pick fewer, defend each one</h2>
          <p className="text-muted-foreground">
            Twenty items you actually use will outsell two hundred items pulled from an algorithm. Behind every product, write one sentence that justifies it: how long you've owned it, what it replaced, what it pairs with. That single line raises click-through more reliably than any photography upgrade. The brain trusts specificity; vague enthusiasm reads as advertising.
          </p>

          <h2 className="mt-12 font-serif text-3xl">3. Group by occasion, not by category</h2>
          <p className="text-muted-foreground">
            "Skincare" is a category. "What I take on a long-haul flight" is an occasion. Occasion-led collections sell better because they answer a question the shopper is already asking. Build three to five collections, each anchored to a real moment in your audience's life, and rotate one in every month. The storefronts with the highest repeat-visit rates on Pluggz all follow this pattern.
          </p>

          <h2 className="mt-12 font-serif text-3xl">4. Shorten the path to the cart</h2>
          <p className="text-muted-foreground">
            Every redirect between a product tap and the brand checkout costs you roughly 15% of intent. If your storefront sends shoppers through a link aggregator, then a coupon site, then the brand homepage, you're losing the sale before they reach the buy button. The two-tap rule — product to brand cart — is the single biggest lever you have.
          </p>

          <h2 className="mt-12 font-serif text-3xl">5. Treat the storefront as a magazine, not a catalog</h2>
          <p className="text-muted-foreground">
            A catalog is exhaustive. A magazine is current. Publish small updates: a new "this month I'm loving" row, a short note about a brand you've started buying from, a single product you'd reorder tomorrow. Returning shoppers want to see your taste move. A storefront that hasn't changed in six weeks reads as abandoned, no matter how good the original edit was.
          </p>

          <h2 className="mt-12 font-serif text-3xl">The metric to actually watch</h2>
          <p className="text-muted-foreground">
            Most creators obsess over storefront visits. The number that predicts income, though, is products-per-visit. A shopper who taps three products in one session converts at roughly four times the rate of a shopper who taps one. Build for the second click: surface related items, keep a persistent collection rail, and never end a product page in a dead-end. The storefront that wins is the one that's hard to leave.
          </p>

          <div className="mt-14 rounded-3xl border border-border/60 bg-gradient-to-br from-card via-background to-card p-8 text-center">
            <p className="editorial-eyebrow">Keep reading</p>
            <h3 className="mt-3 font-serif text-2xl">More from the Journal</h3>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
              <Link to="/journal" className="group inline-flex items-center gap-2 border-b border-foreground pb-1 text-sm tracking-wide">
                Back to the Journal
                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </article>
  );
}